import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import ConfirmDialog from "../ConfirmDialog"
import {
  useLazyGetAdminExtensionTokenUserQuery,
  useRevokeAdminExtensionTokenSingleMutation,
} from "../../../../../app/services/book/admin_extension_tokens"
import { formatDate } from "../../utils/date"

const UserDetailPanel = ({ userId, onClose, onGenerate, onRevokeAll }) => {
  const { t } = useTranslation()
  const [fetchUser, { data, isFetching }] = useLazyGetAdminExtensionTokenUserQuery()
  const [revokeSingle] = useRevokeAdminExtensionTokenSingleMutation()
  const [confirmTokenId, setConfirmTokenId] = useState(null)

  useEffect(() => {
    if (userId) fetchUser(userId)
  }, [userId, fetchUser])

  if (!userId) return null

  const user = data?.user
  const tokens = data?.tokens || []
  const stats = data?.stats

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-50 shadow-xl
                       overflow-y-auto border-l border-[#E7EAEF]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E7EAEF]">
          <h2 className="text-base font-semibold text-[#1F2937]">
            {t("admin_extension_tokens.panel.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-[#9AA5B4] hover:text-[#1F2937] text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {isFetching && !user && (
          <div className="text-center py-16 text-[#9AA5B4]">
            {t("admin_extension_tokens.panel.loading")}
          </div>
        )}

        {user && (
          <div className="px-5 py-4 flex flex-col gap-6">
            <div>
              <div className="text-sm font-medium text-[#1F2937]">{user.name || user.email}</div>
              <div className="text-xs text-[#9AA5B4]">{user.email}</div>
              {user.company_name && (
                <div className="text-xs text-[#9AA5B4] mt-0.5">{user.company_name}</div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => onGenerate(user)}
                className="flex-1 px-3 py-2 rounded-xl bg-[#1F2937] text-white text-sm font-medium
                           hover:bg-[#111827] transition-colors"
              >
                {t("admin_extension_tokens.panel.generate_new")}
              </button>
              <button
                onClick={() => onRevokeAll(user)}
                disabled={!tokens.some((tk) => tk.active)}
                className="flex-1 px-3 py-2 rounded-xl border border-[#FCE4E4] text-[#EF4444] text-sm font-medium
                           hover:bg-[#FEF2F2] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {t("admin_extension_tokens.panel.revoke_all")}
              </button>
            </div>

            {stats && (
              <div>
                <h3 className="text-xs font-semibold text-[#9AA5B4] uppercase tracking-wide mb-2">
                  {t("admin_extension_tokens.panel.stats_title")}
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-[#F4F6F9] rounded-xl px-3 py-2">
                    <div className="text-lg font-semibold text-[#1F2937]">
                      {stats.imports_total}
                    </div>
                    <div className="text-[11px] text-[#9AA5B4]">
                      {t("admin_extension_tokens.stats.imports_total")}
                    </div>
                  </div>
                  <div className="bg-[#F4F6F9] rounded-xl px-3 py-2">
                    <div className="text-lg font-semibold text-[#1F2937]">
                      {stats.imports_last_30_days}
                    </div>
                    <div className="text-[11px] text-[#9AA5B4]">
                      {t("admin_extension_tokens.stats.imports_last_30_days")}
                    </div>
                  </div>
                </div>

                {stats.by_source?.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    {stats.by_source.map((s) => (
                      <div
                        key={`${s.source_name}-${s.source_url}`}
                        className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-[#F4F6F9]"
                      >
                        <span className="text-[#1F2937] truncate">{s.source_name}</span>
                        <span className="text-[#9AA5B4]">{s.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <h3 className="text-xs font-semibold text-[#9AA5B4] uppercase tracking-wide mb-2">
                {t("admin_extension_tokens.panel.history_title")}
              </h3>
              <div className="flex flex-col gap-2">
                {tokens.map((token) => (
                  <div
                    key={token.id}
                    className="flex items-center justify-between px-3 py-2 rounded-xl border border-[#E7EAEF]"
                  >
                    <div>
                      <div className="text-sm text-[#1F2937] font-medium">
                        {token.token_prefix}…
                      </div>
                      <div className="text-[11px] text-[#9AA5B4]">
                        {t("admin_extension_tokens.panel.created")}: {formatDate(token.created_at)}
                      </div>
                      <div className="text-[11px] text-[#9AA5B4]">
                        {t("admin_extension_tokens.panel.last_used")}:{" "}
                        {formatDate(token.last_used_at)}
                      </div>
                      {!token.active && (
                        <div className="text-[11px] text-[#9AA5B4]">
                          {t("admin_extension_tokens.panel.revoked_at")}:{" "}
                          {formatDate(token.revoked_at)}
                        </div>
                      )}
                    </div>

                    {token.active ? (
                      <button
                        onClick={() => setConfirmTokenId(token.id)}
                        className="text-xs font-medium text-[#EF4444] px-2.5 py-1.5 rounded-lg border border-[#FCE4E4]
                                   hover:bg-[#FEF2F2] transition-colors"
                      >
                        {t("admin_extension_tokens.panel.revoke")}
                      </button>
                    ) : (
                      <span className="text-[11px] text-[#9AA5B4]">
                        {t("admin_extension_tokens.panel.revoked_badge")}
                      </span>
                    )}
                  </div>
                ))}

                {tokens.length === 0 && (
                  <div className="text-xs text-[#9AA5B4] text-center py-4">
                    {t("admin_extension_tokens.panel.no_tokens")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmTokenId}
        title={t("admin_extension_tokens.panel.revoke_token_confirm_title")}
        message={t("admin_extension_tokens.panel.revoke_token_confirm_message")}
        confirmLabel={t("admin_extension_tokens.row.revoke")}
        danger
        onConfirm={async () => {
          await revokeSingle({ userId, tokenId: confirmTokenId }).unwrap()
          setConfirmTokenId(null)
        }}
        onCancel={() => setConfirmTokenId(null)}
      />
    </>
  )
}

export default UserDetailPanel
